import React, { useEffect, useMemo, useState } from "react";
import GlassCard from "../../components/GlassCard";
import FooterPlatform from "../components/FooterPlatform";
import "../styles/formPage.css";
import BodyRegionMap from "../components/BodyRegionMap";
import { useNavigate } from "react-router-dom";
import { useCurrentLang } from "../../hooks/useCurrentLang";
import { submitIntake } from "../api/intakeApi";  
import { useAuth } from "../../auth";
const DETAIL_FIELDS = [
  {
    id: "problem_main_type",
    label: "What is the main problem?",
    type: "select",
    options: [
      { value: "pain", label: "Pain" },
      { value: "stiffness", label: "Stiffness" },
      { value: "weakness", label: "Weakness" },
      { value: "numbness_tingling", label: "Numbness / tingling" },
      { value: "instability_giving_way", label: "Instability / giving way" },
      { value: "swelling", label: "Swelling" },
      { value: "balance_dizziness", label: "Balance / dizziness" },
    ],
  },
  {
    id: "problem_start_time",
    label: "When did this problem start?",
    type: "select",
    options: [
      { value: "today", label: "Today" },
      { value: "days_1_7", label: "1–7 days" },
      { value: "weeks_1_6", label: "1–6 weeks" },
      { value: "weeks_6_12", label: "6–12 weeks" },
      { value: "months_3_plus", label: "3+ months" },
    ],
  },
  {
    id: "problem_onset_mode",
    label: "How did it start?",
    type: "select",
    options: [
      { value: "sudden_injury", label: "Sudden injury" },
      { value: "gradual_onset", label: "Gradual onset" },
      { value: "after_exercise_sport", label: "After exercise / sport" },
      { value: "after_surgery", label: "After surgery" },
      { value: "after_accident_fall", label: "After accident / fall" },
      { value: "after_lifting_twisting", label: "After lifting / twisting" },
    ],
  },
  {
    id: "problem_trend",
    label: "Is the problem getting better, worse, or staying the same?",
    type: "select",
    options: [
      { value: "better", label: "Better" },
      { value: "same", label: "Same" },
      { value: "worse", label: "Worse" },
    ],
  },
  {
    id: "problem_severity_now",
    label: "How severe is it right now?",
    type: "scale",
    min: 1,
    max: 5,
  },
  {
    id: "problem_worse_by",
    label: "What makes it worse?",
    type: "multiselect",
    options: [
      { value: "walking", label: "Walking" },
      { value: "sitting", label: "Sitting" },
      { value: "standing", label: "Standing" },
      { value: "bending", label: "Bending" },
      { value: "lifting", label: "Lifting" },
      { value: "stairs", label: "Stairs" },
      { value: "running", label: "Running" },
      { value: "sleeping", label: "Sleeping" },
      { value: "coughing_sneezing", label: "Coughing / sneezing" },
      { value: "overhead_movement", label: "Overhead movement" },
      { value: "no_clear_trigger", label: "No clear trigger" },
    ],
  },
  {
    id: "problem_better_by",
    label: "What makes it better?",
    type: "multiselect",
    options: [
      { value: "rest", label: "Rest" },
      { value: "movement", label: "Movement" },
      { value: "lying_down", label: "Lying down" },
      { value: "heat", label: "Heat" },
      { value: "ice", label: "Ice" },
      { value: "medication", label: "Medication" },
      { value: "stretching", label: "Stretching" },
      { value: "nothing_helps", label: "Nothing helps" },
    ],
  },
  {
    id: "problem_function_limit",
    label: "Which function is most limited right now?",
    type: "select",
    options: [
      { value: "walking", label: "Walking" },
      { value: "sitting", label: "Sitting" },
      { value: "standing", label: "Standing" },
      { value: "lifting", label: "Lifting" },
      { value: "sleep", label: "Sleep" },
      { value: "work", label: "Work" },
      { value: "sport", label: "Sport" },
      { value: "stairs", label: "Stairs" },
      { value: "daily_tasks", label: "Daily tasks" },
      { value: "other", label: "Other" },
    ],
  },
];

const RED_FLAG_FIELDS = [
  {
    id: "rf_major_trauma",
    label: "Did this start after a major accident, fall, or significant trauma?",
  },
  {
    id: "rf_recent_surgery",
    label: "Was there surgery on this body area in the last 6 months?",
  },
  {
    id: "rf_systemic_symptoms",
    label: "Do you currently have fever, chills, night sweats, or feel generally unwell?",
  },
  {
    id: "rf_unexplained_weight_loss",
    label: "Have you had unexplained weight loss recently?",
  },
  {
    id: "rf_cancer_history",
    label: "Do you have a history of cancer?",
  },
  {
    id: "rf_constant_night_pain",
    label: "Is the pain constant, severe at night, or not changing with movement/position?",
  },
  {
    id: "rf_new_numbness_weakness",
    label: "Do you have new numbness, tingling, or weakness?",
  },
  {
    id: "rf_bilateral_limb_symptoms",
    label: "Do you have weakness or symptoms in both legs or both arms?",
  },
  {
    id: "rf_bowel_bladder_loss",
    label: "Do you have any new problems controlling your bladder or bowel?",
  },
  {
    id: "rf_saddle_anesthesia",
    label: "Do you have numbness in the groin, genitals, inner thighs, or around the buttocks / saddle area?",
  },
  {
    id: "rf_new_urine_control",
    label: "Are you finding it newly difficult to start urinating or control urine flow?",
  },
  {
    id: "rf_fracture_risk",
    label: "Do you have osteoporosis, long-term steroid use, or known high fracture risk?",
  },
  {
    id: "rf_neuro_dizziness_vision",
    label: "Do you have unexplained dizziness, fainting, major balance problems, or vision changes?",
  },
];


const FormPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedBodyRegions, setSelectedBodyRegions] = useState([]);
  const [formState, setFormState] = useState({
    body_location: [],
    problem_main_type: "",
    problem_start_time: "",
    problem_onset_mode: "",
    problem_trend: "",
    problem_severity_now: 1,
    problem_worse_by: [],
    problem_better_by: [],
    problem_function_limit: "",
    rf_major_trauma: null,
    rf_recent_surgery: null,
    rf_systemic_symptoms: null,
    rf_unexplained_weight_loss: null,
    rf_cancer_history: null,
    rf_constant_night_pain: null,
    rf_new_numbness_weakness: null,
    rf_bilateral_limb_symptoms: null,
    rf_bowel_bladder_loss: null,
    rf_saddle_anesthesia: null,
    rf_new_urine_control: null,
    rf_fracture_risk: null,
    rf_neuro_dizziness_vision: null,
  });
  const [stepError, setStepError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const { refreshUser } = useAuth();

  const isCurrentStepValid = () => {
  if (activeStep.key === "intro" || activeStep.key === "final") {
    return true;
  }

  if (activeStep.key === "problem") {
    return Array.isArray(selectedBodyRegions) && selectedBodyRegions.length > 0;
  }

  if (activeStep.key === "details") {
    return DETAIL_FIELDS.every((field) => {
      const value = formState[field.id];

      if (field.type === "multiselect") {
        return Array.isArray(value) && value.length > 0;
      }

      if (field.type === "scale") {
        return value !== null && value !== undefined && value !== "";
      }

      return value !== "";
    });
  }
  

  if (activeStep.key === "redFlags") {
    return RED_FLAG_FIELDS.every((field) => formState[field.id] !== null);
  }

  return true;
  };


  const navigate = useNavigate();
  const currentLang = useCurrentLang();

  const steps = useMemo(
    () => [
      {
        key: "intro",
        title: "Before we begin",
        progress: 0,
        cta: "Start",
        showArrows: false,
      },
      {
        key: "problem",
        title: "Find the problem",
        progress: 25,
        cta: "Continue",
        showArrows: true,
      },
      {
        key: "details",
        title: "More information",
        progress: 50,
        cta: "Review red flags",
        showArrows: true,
      },
      {
        key: "redFlags",
        title: "Red flags",
        progress: 75,
        cta: "Finish",
        showArrows: true,
      },
      {
        key: "final",
        title: "All set",
        progress: 100,
        cta: "",
        showArrows: false,
      },
    ],
    []
  );

  const activeStep = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const goNext = async () => {
    const valid = isCurrentStepValid();

    if (!valid) {
      setStepError("Please answer all required questions before continuing.");
      return;
    }

    setStepError("");

    if (activeStep.key === "redFlags") {
      const result = await handleSubmit();
      if (!result) return;
      setCurrentStep((prev) => prev + 1);
      return;
    }

    if (!isLastStep) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const goBack = () => {
    setStepError("");
    if (!isFirstStep) setCurrentStep((prev) => prev - 1);
  };

  const goPlatform = () => {
    navigate(`/${currentLang}/platform`);
  };

  const handleFieldChange = (fieldId, value) => {
  setStepError("");
  setFormState((prev) => ({
    ...prev,
    [fieldId]: value,
  }));
  };

  const toggleMultiSelectValue = (fieldId, value) => {
    setStepError("");
    setFormState((prev) => {
      const currentValues = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value];

      return {
        ...prev,
        [fieldId]: nextValues,
      };
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError("");

    try {
      const json = await submitIntake({
        formState,
        selectedBodyRegions,
        currentLang,
      });

      await refreshUser();

      return json;
    } catch (error) {
      console.error("Intake submit error:", error);
      setSubmitError(error.message || "Failed to save intake.");
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderProgressLabels = () => (
    <div className="intake-progress-labels" aria-hidden="true">
      {steps.map((step, index) => (
        <span
          key={step.key}
          className={`intake-progress-label ${index <= currentStep ? "active" : ""}`}
        >
          {step.title}
        </span>
      ))}
    </div>
  );

  const renderSelectField = (field) => (
  <div key={field.id} className="intake-question-block">
    <label className="intake-question-label" htmlFor={field.id}>
      {field.label} <span className="intake-required-star">*</span>
    </label>
    <select
      id={field.id}
      className="intake-select"
      value={formState[field.id]}
      onChange={(e) => handleFieldChange(field.id, e.target.value)}
    >
      <option value="">Select an option</option>
      {field.options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
  );

  const renderScaleField = (field) => (
  <div key={field.id} className="intake-question-block">
    <label className="intake-question-label" htmlFor={field.id}>
      {field.label} <span className="intake-required-star">*</span>
    </label>

    <div className="intake-scale-wrap">
      <input
        id={field.id}
        type="range"
        min={field.min}
        max={field.max}
        step="1"
        value={formState[field.id]}
        className="intake-range"
        onChange={(e) => handleFieldChange(field.id, Number(e.target.value))}
      />
      <div className="intake-scale-row">
        {[1, 2, 3, 4, 5].map((num) => (
          <span
            key={num}
            className={`intake-scale-pill ${
              Number(formState[field.id]) === num ? "active" : ""
            }`}
          >
            {num}
          </span>
        ))}
      </div>
    </div>
  </div>
  );

  const renderMultiSelectField = (field) => (
  <div key={field.id} className="intake-question-block">
    <div className="intake-question-label">
      {field.label} <span className="intake-required-star">*</span>
    </div>
    <div className="intake-chip-group">
      {field.options.map((option) => {
        const isActive = formState[field.id].includes(option.value);

        return (
          <button
            key={option.value}
            type="button"
            className={`intake-chip ${isActive ? "active" : ""}`}
            onClick={() => toggleMultiSelectValue(field.id, option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
  );

  const renderBooleanField = (field) => (
  <div key={field.id} className="intake-question-block">
    <div className="intake-question-label">
      {field.label} <span className="intake-required-star">*</span>
    </div>
    <div className="intake-radio-row">
      <label className={`intake-radio-card ${formState[field.id] === true ? "active" : ""}`}>
        <input
          type="radio"
          name={field.id}
          checked={formState[field.id] === true}
          onChange={() => handleFieldChange(field.id, true)}
        />
        <span>Yes</span>
      </label>

      <label className={`intake-radio-card ${formState[field.id] === false ? "active" : ""}`}>
        <input
          type="radio"
          name={field.id}
          checked={formState[field.id] === false}
          onChange={() => handleFieldChange(field.id, false)}
        />
        <span>No</span>
      </label>
    </div>
  </div>
  );

  const renderStepContent = () => {
    switch (activeStep.key) {
      case "intro":
        return (
          <div className="intake-screen-content">
            <div className="intake-badge">Important step before your plan</div>

            <h1 className="intake-main-title">
              Help us understand your situation first
            </h1>

            <p className="intake-lead">
              Before Revimo suggests any exercise plan, we first need a clear
              overview of your issue, your symptoms, and possible warning signs.
            </p>

            <div className="intake-info-grid">
              <div className="intake-info-block">
                <h3>Why this matters</h3>
                <p>
                  This helps us guide users more safely and avoid assigning plans
                  too early when additional caution may be needed.
                </p>
              </div>

              <div className="intake-info-block">
                <h3>What happens next</h3>
                <p>
                  You will go through a short intake in 3 parts: identifying the
                  problem, adding more detail, and checking red flags.
                </p>
              </div>

              <div className="intake-info-block">
                <h3>What to expect</h3>
                <p>
                  You can select one or more body regions first, then answer
                  questions about symptoms and possible warning signs.
                </p>
              </div>
            </div>
          </div>
        );

      case "problem":
        return (
          <div className="intake-screen-content">
            <p className="intake-section-intro">
              Select at least one body area where the problem is present.
            </p>

            <div className="intake-question-block">
              <div className="intake-question-label">
                Which body areas are involved? <span className="intake-required-star">*</span>
              </div>
              <BodyRegionMap
                value={selectedBodyRegions}
                onChange={(next) => {
                  setStepError("");
                  setSelectedBodyRegions(next);
                }}
              />
            </div>
          </div>
        );

      case "details":
        return (
          <div className="intake-screen-content">
            <p className="intake-section-intro">
              Answer a few short questions so Revimo can better understand the
              problem and how it affects you.
            </p>

            <div className="intake-fields-stack">
              {DETAIL_FIELDS.map((field) => {
                if (field.type === "select") return renderSelectField(field);
                if (field.type === "scale") return renderScaleField(field);
                if (field.type === "multiselect") return renderMultiSelectField(field);
                return null;
              })}
            </div>
          </div>
        );

      case "redFlags":
        return (
          <div className="intake-screen-content">
            <p className="intake-section-intro">
              These questions help identify warning signs that may require
              additional caution before any automated plan is assigned.
            </p>

            <div className="intake-alert-shell">
              <div className="intake-alert-box">
                <h3>Red flag screening</h3>
                <p>
                  If any important warning sign is reported, Revimo can later use
                  that information to trigger a stop warning or another safe next
                  step.
                </p>
              </div>
            </div>

            

              

            <div className="intake-fields-stack">
              {RED_FLAG_FIELDS.map((field) => renderBooleanField(field))}
            </div>
          </div>
        );

      case "final":
        return (
          <div className="intake-screen-content">
            <div className="intake-badge intake-badge-success">
              Intake completed
            </div>

            <h2 className="intake-main-title final-title">
              Thank you for completing this check-in
            </h2>

            <p className="intake-lead">
              Your responses help us understand your situation before any
              exercise plan is assigned. This reduces risk, improves relevance,
              and helps Revimo adapt better over time.
            </p>

            <div className="intake-final-note">
              <h3>What comes next</h3>
              <p>
                Your intake has been recorded. You can now continue back to the
                platform.
              </p>
            </div>

            <div className="intake-final-actions">
              <button
                type="button"
                className="intake-primary-button"
                onClick={goPlatform}
              >
                Back to platform
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section className="intake-page-shell">
      <div className="intake-form-wrap">
        <div
          className="intake-progress-wrap"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={activeStep.progress}
          aria-label="Form completion progress"
        >
          <div className="intake-progress-topline">
            <span className="intake-progress-copy">Progress</span>
            <span className="intake-progress-percent">{activeStep.progress}%</span>
          </div>

          <div className="intake-progress-track">
            <div
              className="intake-progress-fill"
              style={{ width: `${activeStep.progress}%` }}
            />
          </div>

          {renderProgressLabels()}
        </div>

        <GlassCard className="intake-glass-card">
          <div className="intake-card-inner">
            <div className="intake-card-top">
              <div className="intake-top-row">
                <div className="intake-step-meta">
                  <span className="intake-step-counter">
                    Step {currentStep + 1} of {steps.length}
                  </span>
                  <h2 className="intake-step-title">{activeStep.title}</h2>
                </div>

                {activeStep.showArrows ? (
                  <div className="intake-arrow-controls">
                    <button
                      type="button"
                      className="intake-arrow-button"
                      onClick={goBack}
                      aria-label="Go to previous section"
                    >
                      <span>←</span>
                    </button>

                    <button
                      type="button"
                      className="intake-arrow-button"
                      onClick={goNext}
                      aria-label="Go to next section"
                    >
                      <span>→</span>
                    </button>
                  </div>
                ) : (
                  <div className="intake-arrow-controls intake-arrow-controls-hidden" />
                )}
              </div>
            </div>

            <div className="intake-card-body">{renderStepContent()}</div>
            {stepError ? (
              <div className="intake-step-error" role="alert">
                {stepError}
              </div>    
            ) : null}

            <div className="intake-card-footer">
              {!isLastStep ? (
                <button
                  type="button"
                  className="intake-primary-button"
                  onClick={goNext}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : activeStep.cta}
                </button>
              ) : (
                <div className="intake-footer-spacer" />
              )}
            </div>
          </div>
        </GlassCard>
      </div>
      {submitError ? (
        <div className="intake-step-error" role="alert">
          {submitError}
        </div>
      ) : null}

      <footer className="form-page-footer">
        <FooterPlatform />
      </footer>
    </section>
  );
};

export default FormPage;